import { IconDefinition } from '@fortawesome/fontawesome-common-types';
import { faAnalytics } from '@fortawesome/pro-light-svg-icons';
import {
  faAddressBook,
  faBell,
  faBoxArchive,
  faBriefcase,
  faBuilding,
  faChartBar,
  faCircleQuestion,
  faCog,
  faEnvelope,
  faEnvelopeOpen,
  faEnvelopes,
  faFiles,
  faLink,
  faPalette,
  faSlidersH,
  faTools,
  faUser,
  faUsers,
  faUserShield,
  faUsersRectangle,
} from '@fortawesome/pro-regular-svg-icons';
import { faKey } from '@fortawesome/pro-solid-svg-icons';
import { PermissionsKeys } from '@gen2/types/permissions';
import { Contacts } from './sub-menu/contacts';
import { InviteSettings } from './sub-menu/invite-settings';
import { Invites } from './sub-menu/invites';
import { Settings } from './sub-menu/settings';

export type MenuItem = {
  id: string;
  to?: string;
  title?: string;
  icon?: IconDefinition;
  featureFlag?: string | string[];
  permission?: PermissionsKeys | PermissionsKeys[]; // one of the key must be valid
  target?: string;
  isMaster?: boolean;
};

export const mainMenu: MenuItem[] = [
  {
    id: 'invites',
    title: 'Invites',
    icon: faEnvelopes,
    permission: PermissionsKeys.LIST_INVITE,
  },
  {
    id: 'templates',
    title: 'Templates',
    to: '/templates',
    icon: faFiles,
    permission: PermissionsKeys.LIST_TEMPLATE,
  },
  // {
  //   id: 'team',
  //   title: 'Team',
  //   icon: faUsers,
  //   permission: [PermissionsKeys.LIST_USER, PermissionsKeys.LIST_TEAM],
  // },
  {
    id: 'contacts',
    title: 'Contacts',
    icon: faAddressBook,
    permission: PermissionsKeys.LIST_CONTACT,
  },
  {
    id: 'management-insights',
    title: 'Reporting',
    icon: faChartBar,
    to: '/reporting',
    permission: [
      PermissionsKeys.SHOW_REPORTING,
      PermissionsKeys.SHOW_MANAGEMENT_INSIGHTS,
    ],
  },
];

export const extras: MenuItem[] = [
  {
    id: 'settings',
    title: 'Settings',
    icon: faCog,
  },
  {
    id: 'help',
    title: 'Help',
    icon: faCircleQuestion,
    to: 'https://help.fileinvite.com/articles',
    target: '_blank',
  },
];

// ===============================================
// Sub Menu
// ===============================================
export const subMenus: Record<MenuItem['id'], React.ReactNode> = {
  invites: <Invites />,
  templates: <div>Templates</div>,
  contacts: <Contacts />,
  updates: <div>Updates</div>,
  settings: <Settings />,
  inviteSettings: <InviteSettings />,
  managementInsights: <div>Management Insights</div>,
};

export const invites: MenuItem[] = [
  {
    id: 'manageInvites',
    to: '/invite-listing',
    title: 'Manage Invites',
    icon: faEnvelopes,
    permission: PermissionsKeys.LIST_INVITE,
  },
  {
    id: 'draftInvites',
    to: '/draft-listing',
    title: 'Draft Invites',
    icon: faEnvelopeOpen,
    permission: PermissionsKeys.LIST_INVITE,
  },
  {
    id: 'archivedInvites',
    to: '/archived-listing',
    title: 'Archived Invites',
    icon: faBoxArchive,
    permission: PermissionsKeys.LIST_INVITE,
  },
  {
    id: 'collationHistory',
    to: '/invites/collation-history',
    title: 'Collation History',
    icon: faFiles,
    permission: PermissionsKeys.LIST_COLLATE_HISTORY,
  },
];

export const settings: MenuItem[] = [
  {
    id: 'account',
    title: 'Advanced Settings',
    to: '/account',
    icon: faBriefcase,
    permission: [
      PermissionsKeys.SHOW_CUSTOM_DOMAIN,
      PermissionsKeys.SHOW_ACCOUNT_SSO,
    ],
  },
  {
    id: 'organisation-settings',
    title: 'Organisation Settings',
    to: '/organisation-settings',
    icon: faBuilding,
    isMaster: true,
    permission: PermissionsKeys.SHOW_ORGANISATION_SSO,
  },
  {
    id: 'branding',
    title: 'Branding',
    to: '/branding',
    icon: faPalette,
    permission: PermissionsKeys.LIST_BRANDING,
  },
  {
    id: 'integrations',
    title: 'Integrations',
    to: '/integrations',
    icon: faLink,
    permission: PermissionsKeys.CREATE_INVITE,
  },
  {
    id: 'preferences',
    title: 'File Naming Preferences',
    to: '/file-options',
    icon: faSlidersH,
    permission: PermissionsKeys.LIST_NAMING_CONVENTION,
  },
  {
    id: 'profile',
    to: '/user-settings',
    title: 'Profile',
    icon: faUser,
  },
  {
    id: 'workspaces',
    to: '/workspaces',
    title: 'Workspace',
    icon: faTools,
    permission: PermissionsKeys.LIST_VIEW,
  },
  {
    id: 'borrower-email',
    to: '/borrower-email-template-settings',
    title: 'Borrower Email',
    icon: faEnvelope,
    permission: [
      PermissionsKeys.SHOW_NOTIFICATION_TEMPLATE,
      PermissionsKeys.LIST_NOTIFICATION_PLACEHOLDER,
      PermissionsKeys.UPSERT_NOTIFICATION_TEMPLATE,
    ],
  },
  {
    id: 'roles-permissions',
    to: '/roles-permissions',
    title: 'Roles & Permissions',
    icon: faUsers,
    permission: PermissionsKeys.CREATE_ROLE,
  },
  {
    id: 'portal-authentication',
    to: '/portal-authentication',
    title: 'Portal Authentication',
    icon: faKey,
    permission: PermissionsKeys.SHOW_PORTAL_AUTHENTICATION,
    featureFlag: 'portal_authentication',
  },
  {
    id: 'notification-preferences',
    to: '/notification-preferences',
    title: 'Notification Preferences',
    icon: faBell,
    // permission: [PermissionsKeys.SHOW_NOTIFICATION_PREFERENCE, PermissionsKeys.UPDATE_NOTIFICATION_PREFERENCE],
  },
  {
    id: 'manageTeamMembers',
    to: '/team-members',
    title: 'Team Members',
    icon: faUser,
    permission: PermissionsKeys.LIST_USER,
  },
  {
    id: 'manageTeams',
    to: '/teams',
    title: 'Teams',
    icon: faUsers,
    permission: PermissionsKeys.LIST_TEAM,
  },
  {
    id: 'dataRetention',
    to: '/data-retention',
    title: 'Data Retention',
    icon: faUserShield,
    featureFlag: 'data_retention',
    permission: PermissionsKeys.SHOW_DATA_RETENTION,
  },
  {
    id: 'usageAndBilling',
    to: '/usage-and-billing',
    title: 'Usage & Billing',
    icon: faAnalytics,
    featureFlag: 'usage_and_billing',
    // permission: PermissionsKeys.SHOW_DATA_RETENTION,
  },
];

// TODO: Remove if not needed
export const team: MenuItem[] = [
  {
    id: 'manageTeamMembers',
    to: '/team-members',
    title: 'Team Members',
    icon: faUser,
    permission: PermissionsKeys.LIST_USER,
  },
  {
    id: 'manageTeams',
    to: '/teams',
    title: 'Teams',
    icon: faUsers,
    permission: PermissionsKeys.LIST_TEAM,
  },
];

export const settingsWithoutAccount = settings.slice(1);
// export const team: MenuItem[] = [
//   {
//     id: 'manageTeamMembers',
//     to: '/team-members',
//     title: 'Team Members',
//     icon: faUser,
//     permission: PermissionsKeys.LIST_USER,
//   },
//   {
//     id: 'manageTeams',
//     to: '/teams',
//     title: 'Teams',
//     icon: faUsers,
//     permission: PermissionsKeys.LIST_TEAM,
//   },
// ];

export const contacts: MenuItem[] = [
  {
    id: 'manageContacts',
    to: '/contacts',
    title: 'Contacts',
    icon: faAddressBook,
    permission: PermissionsKeys.LIST_CONTACT,
  },
  {
    id: 'manageContactGroups',
    to: '/contact-groups',
    title: 'Contact Groups',
    icon: faUsersRectangle,
    permission: PermissionsKeys.LIST_CONTACT_GROUP,
  },
];
